import React, {useState, useEffect} from "react";
import {
  Button,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Paper,
  Switch,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { Alert, AlertTitle } from '@mui/material';
import {useClientFilter} from "../../hooks/ClientFilterProvider";
import { confirmDialog } from "../common/ConfirmDialog";
import {Form, Formik, FormikProps} from "formik";
import { batchEmailSchema, BatchEmailModel } from "../../core/api/email/BatchEmailModel";
import {isEmptyFilter} from "../../core/api/clients/ClientFilter";
import {useNotifications} from "../../hooks/NotificationProvider";
import {useNavigate} from "react-router-dom";
import qs from "qs";
import SubjectInput from "./SubjectInput";
import FileSelector from "./FileSelector";
import { NumericFormat } from "react-number-format";
import { DatePicker } from "../common/DatePicker";
import { translations } from "../../translations/common";
import TazEditor from "./TazEditor";

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  position: 'absolute',
  marginTop: 0,
  fontSize: '0.75rem',
}));

const FormFieldWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  '& .MuiFormHelperText-root': {
    position: 'absolute',
    bottom: '-20px',
    left: 0,
    margin: 0,
  }
});

const ContentContainer = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  '& .MuiGrid-root': {
    padding: 0,
    marginBottom: theme.spacing(3)  // Add space for error messages
  }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const EmailForm: React.FC = (props) => {
  const filter = useClientFilter();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customValidation, setCustomValidation] = useState<{
    filterError: null | string;
  }>({filterError: null});
  const [initialValues] = useState(() => {
    const model = BatchEmailModel.createDefault();
    // Ensure content has a subject field initialized
    if (!model.content) {
      model.content = {
        subject: "",
        htmlText: "",
        attachments: [],
        important: false
      };
    }
    return model;
  });

  useEffect(() => {
    if (!isEmptyFilter(filter.filter)) {
      setCustomValidation({filterError: null});
    }
  }, [filter.filter]);

  async function handleEmail(value: BatchEmailModel) {
    if (isEmptyFilter(filter.filter)) {
      setCustomValidation({
        filterError: "Laiškų siuntimui filtras yra privalomas!",
      });
      return;
    }
    setIsSubmitting(true);
    confirmDialog({
      onCancel: () => { setIsSubmitting(false)},
      action: () => {
        BatchEmailModel.sendBatch(value, filter.filter)
        .then(() => {
          notifications.notify({
            title: "Pradėta siųsti",
            message: "Laiškai pradėti siųsti",
            level: "success",
          });
        })
        .then(() => {
          navigate({search: qs.stringify({tab: "results"})}, {replace: true});
        })
        .catch((error) => {
          console.error("Error sending emails:", error);
          notifications.notify({
            title: "Klaida",
            message: "Įvyko klaida siunčiant laiškus",
            level: "error",
          });
        });
        setIsSubmitting(false);
      },
      actionName: "Siųsti",
      body: `Ar tikrai siųsti laišką pasirinktiem klientams?`,
      title: "Siųsti laiškus?",
    });
  }

  return (
    <StyledPaper>
      <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validateOnBlur={false}
          validateOnChange={false}
          validateOnMount={false}
          validationSchema={batchEmailSchema}
          onSubmit={(values, formikHelpers) => {
            if (isEmptyFilter(filter.filter)) {
              setCustomValidation({
                filterError: "Laiškų siuntimui filtras yra privalomas!",
              });
              // Reset the submitting state so the button becomes enabled again
              return;
            }

            // Clear any previous filter errors
            setCustomValidation({ filterError: null });

            // Proceed with email sending
            handleEmail(values);
          }}
      >
        {(props: FormikProps<BatchEmailModel>) => {
          const {
            values,
            errors,
            handleBlur,
            handleChange,
            setFieldValue,
            setFieldTouched,
            validateForm,
            setErrors,
          } = props;

          const handleFormSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            // Validate the form
            const validationErrors = await validateForm();

            // If there are no errors, submit the form
            if (Object.keys(validationErrors).length === 0) {
              props.submitForm();
            } else {
              setErrors(validationErrors);
            }
          };

          return (
            <Form onSubmit={handleFormSubmit}>
              <ContentContainer
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="stretch"
                  spacing={2}
              >
                {customValidation.filterError && (
                    <Grid size={12}>
                      <Alert severity="error">
                        <AlertTitle>Klaida</AlertTitle>
                        {customValidation.filterError}
                      </Alert>
                    </Grid>
                )}

                <Grid size={12}>
                  <FormFieldWrapper>
                    <SubjectInput
                      id="content.subject"
                      name="content.subject"
                      onChange={async (value) => {
                        await setFieldValue("content.subject", value, true);
                      }}
                      value={values.content?.subject || ""}
                      error={Boolean(errors.content?.subject)}
                      helperText={errors.content?.subject || ""}
                      onBlur={() => setFieldTouched("content.subject", true, false)}
                    />
                  </FormFieldWrapper>
                </Grid>

                <Grid size={12}>
                  <FormFieldWrapper>
                    <TazEditor
                        id="content.htmlText"
                        name="content.htmlText"
                        value={values.content.htmlText}
                        onChange={async (value) => {
                          let content = value.trim();
                          if (value.trim() === '<p><br></p>') {
                            content ='';
                          }
                          await setFieldValue("content.htmlText", content);
                          await setFieldTouched("content.htmlText", true, true);
                        }}
                        onImportantToggle={async (isImportant) => {
                          await setFieldValue("content.important", isImportant);
                       }}
                    />
                    {errors.content?.htmlText && (
                        <StyledFormHelperText error>
                          {errors.content?.htmlText}
                        </StyledFormHelperText>
                    )}
                  </FormFieldWrapper>
                </Grid>

                <Grid size={12}>
                  <FileSelector
                      id="content.attachments"
                      name="content.attachments"
                      value={values.content.attachments}
                      onChange={setFieldValue}
                  />
                </Grid>

                <Grid size={12}>
                  <FormGroup row>
                    <FormControlLabel
                        control={
                          <Switch
                              value={values.withInvoice}
                              checked={values.withInvoice}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="withInvoice"
                              id="withInvoice"
                          />
                        }
                        label="Siųsti išankstinę sąskaitą"
                    />
                  </FormGroup>
                </Grid>

                {values.withInvoice && (
                    <Grid size={12}>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <FormFieldWrapper>
                            <DatePicker
                              id="invoice.date"
                              name="invoice.date"
                              label={translations.datePickerLabels.date}
                              value={values.invoice?.date}
                              onChange={handleChange}
                              error={!!errors.invoice?.date}
                              helperText={errors.invoice?.date || ""}
                              fullWidth
                            />
                          </FormFieldWrapper>
                        </Grid>
                        <Grid size={12}>
                          <FormFieldWrapper>
                            <DatePicker
                              id="invoice.periodFrom"
                              name="invoice.periodFrom"
                              label={translations.datePickerLabels.periodFrom}
                              value={values.invoice?.periodFrom}
                            onChange={(e) => {
                              const newPeriodFrom = e.target.value;
                              handleChange(e);
                              // Update productPeriod when periodFrom changes
                              if (newPeriodFrom && values.invoice?.periodTo) {
                                setFieldValue("invoice.productPeriod", `${newPeriodFrom} - ${values.invoice.periodTo}`);
                              }
                            }}
                              error={!!errors.invoice?.periodFrom}
                              helperText={errors.invoice?.periodFrom || ""}
                              fullWidth
                            />
                          </FormFieldWrapper>
                        </Grid>
                        <Grid size={12}>
                          <FormFieldWrapper>
                            <DatePicker
                              id="invoice.periodTo"
                              name="invoice.periodTo"
                              label={translations.datePickerLabels.periodTo}
                              value={values.invoice?.periodTo}
                            onChange={(e) => {
                              const newPeriodTo = e.target.value;
                              handleChange(e);
                              // Update productPeriod when periodTo changes
                              if (values.invoice?.periodFrom && newPeriodTo) {
                                setFieldValue("invoice.productPeriod", `${values.invoice.periodFrom} - ${newPeriodTo}`);
                              }
                            }}
                              error={!!errors.invoice?.periodTo}
                              helperText={errors.invoice?.periodTo || ""}
                              fullWidth
                            />
                          </FormFieldWrapper>
                        </Grid>
                        <Grid size={12}>
                          <FormFieldWrapper>
                            <NumericFormat
                                customInput={TextField}
                                name="invoice.price"
                                id="invoice.price"
                                label="Kaina"
                                value={values.invoice?.price ?? ""}
                                fullWidth
                                allowedDecimalSeparators={[".", ","]}
                                helperText={errors.invoice?.price || ""}
                                error={Boolean(errors.invoice?.price)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                          </FormFieldWrapper>
                        </Grid>
                        <Grid size={12}>
                          <TextField
                              name="invoice.jobTitle"
                              id="invoice.jobTitle"
                              label="Išrašęs asmuo:"
                              value={values.invoice?.jobTitle}
                              type="text"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                          />
                        </Grid>
                      <Grid size={12}>
                        <FormFieldWrapper>
                          <TextField
                            name="invoice.product"
                            id="invoice.product"
                            label="Produktas"
                            value={values.invoice?.product}
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            error={!!errors.invoice?.product}
                            helperText={errors.invoice?.product || ""}
                          />
                        </FormFieldWrapper>
                      </Grid>
                      <Grid size={12}>
                        <FormFieldWrapper>
                          <TextField
                            name="invoice.productPeriod"
                            id="invoice.productPeriod"
                            label="Produkto laikotarpis"
                            value={values.invoice?.productPeriod}
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                            error={!!errors.invoice?.productPeriod}
                            helperText={errors.invoice?.productPeriod || ""}
                          />
                        </FormFieldWrapper>
                      </Grid>
                      </Grid>
                    </Grid>
                )}
                <Grid size={12}>
                  <Button
                      type="submit"
                      fullWidth
                      disabled={isSubmitting}
                      variant="contained"
                      color="primary"
                  >
                    Siųsti
                  </Button>
                </Grid>
              </ContentContainer>
            </Form>
          );
        }}
      </Formik>
    </StyledPaper>
  );
};
export default EmailForm;
